import { css, cx } from "styled-system/css";
import type { ButtonHTMLAttributes, ReactNode } from "react";

const base = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "2",
  px: "4",
  py: "2.5",
  rounded: "lg",
  fontSize: "sm",
  fontWeight: "semibold",
  cursor: "pointer",
  border: "none",
  // Solo transform + opacity: no disparan layout ni paint (Emil: animar solo GPU-properties)
  transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease",
  _active: { transform: "scale(0.97)" },
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
  _focusVisible: { outline: "2px solid token(colors.brand.500)", outlineOffset: "2px" },
});

const sizes = {
  sm: css({ px: "3", py: "1.5", fontSize: "xs" }),
  md: css({}),
  lg: css({ px: "5", py: "3", fontSize: "md" }),
};

const variants = {
  primary: css({
    bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
    color: "white",
    boxShadow: "sm",
    _hover: { opacity: 0.95, boxShadow: "md" },
  }),
  secondary: css({
    bg: "bg.muted",
    color: "fg.DEFAULT",
    borderWidth: "1px",
    borderColor: "border.DEFAULT",
    _hover: { bg: "bg.card" },
  }),
  ghost: css({
    bg: "transparent",
    color: "fg.DEFAULT",
    _hover: { bg: "bg.muted" },
  }),
  danger: css({
    bg: "red.600",
    color: "white",
    _hover: { bg: "red.700" },
  }),
};

export type ButtonVariant = keyof typeof variants;
export type ButtonSize = keyof typeof sizes;

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}) {
  return (
    <button type="button" className={cx(base, variants[variant], sizes[size], className)} {...props}>
      {children}
    </button>
  );
}
